import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

export default function Upload() {
  const [file, setFile]               = useState(null);
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [error, setError]             = useState('');
  const [done, setDone]               = useState(false);
  const [suggestedTimes, setSuggestedTimes] = useState([]);
  const fileRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/api/analytics/insights').then(r => {
      const schedule = r.data?.insights?.weeklySchedule;
      if (!schedule) return;
      // Build next 7 days of suggested times
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const suggestions = [];
      const now = new Date();
      for (let i = 0; i < 7; i++) {
        const date = new Date(now);
        date.setDate(now.getDate() + i);
        const dayName = days[date.getDay()];
        const daySchedule = schedule.find(d => d.day === dayName);
        if (daySchedule?.times?.length) {
          // Only take the first recommended time per day
          const time = daySchedule.times[0];
          const [hour, min] = time.split(':');
          const dt = new Date(date);
          dt.setHours(parseInt(hour), parseInt(min || 0), 0, 0);
          if (dt > now) {
            suggestions.push({
              label: `${dayName} at ${time}`,
              value: dt.toISOString().slice(0, 16),
            });
          }
        }
      }
      setSuggestedTimes(suggestions.slice(0, 5));
    }).catch(() => {});
  }, []);

  const handleFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) { setError('Please select a video file'); return; }
    if (f.size > 500 * 1024 * 1024) { setError('File must be under 500MB'); return; }
    setFile(f);
    setError('');
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a video file'); return; }
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      // Get presigned upload URL
      const { data } = await API.post('/api/clips/upload-url', {
        filename: file.name,
        contentType: file.type,
      });

      // Upload directly to S3
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 90));
        };
        xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error('Upload failed'));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', data.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      setProgress(95);

      // Save clip to database
      await API.post('/api/clips/upload', {
        filename: file.name,
        s3_url: data.s3Url,
        title: title || file.name,
        description,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });

      setProgress(100);
      setDone(true);
      setTimeout(() => navigate('/clips'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
      setUploading(false);
    }
  };

  const inputStyle = {
    width:'100%', padding:'10px 14px', background:'var(--surface2)',
    border:'1px solid var(--border)', borderRadius:6, color:'var(--text)',
    fontFamily:'var(--font-body)', fontSize:13, outline:'none',
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Upload a clip</div>
        <div className="page-subtitle">Add an existing video to your scheduling calendar</div>
      </div>

      {done ? (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:28, textAlign:'center'}}>
          <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:18, color:'var(--accent)', marginBottom:8}}>
            ✓ Uploaded successfully
          </div>
          <div style={{color:'var(--text-muted)', fontSize:13}}>Redirecting to clips...</div>
        </div>
      ) : (
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start'}}>

          {/* Left — file drop */}
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              background:'var(--surface)', border:`2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius:12, padding:48, textAlign:'center', cursor:'pointer',
              transition:'all 0.2s', minHeight:280, display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center', gap:12,
            }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { const ev = { target: { files: [f] } }; handleFile(ev); } }}
          >
            <input ref={fileRef} type="file" accept="video/*" style={{display:'none'}} onChange={handleFile}/>
            {file ? (
              <>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
                  <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
                </svg>
                <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16}}>{file.name}</div>
                <div style={{fontSize:12, color:'var(--text-muted)'}}>{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                <div style={{fontSize:12, color:'var(--accent)'}}>Click to change</div>
              </>
            ) : (
              <>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
                <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16}}>Drop your video here</div>
                <div style={{fontSize:13, color:'var(--text-muted)'}}>or click to browse</div>
                <div style={{fontSize:11, color:'var(--text-muted)'}}>MP4, MOV, up to 500MB</div>
              </>
            )}
          </div>

          {/* Right — details */}
          <div style={{display:'flex', flexDirection:'column', gap:14}}>
            <div>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:6}}>Title</div>
              <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Give your clip a title" style={inputStyle}/>
            </div>
            <div>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:6}}>Description</div>
              <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Optional description" rows={3} style={{...inputStyle, resize:'vertical'}}/>
            </div>
            <div>
              <div style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:6}}>Schedule to post <span style={{fontWeight:400, textTransform:'none', letterSpacing:0}}>(optional)</span></div>
              {suggestedTimes.length > 0 && (
                <div style={{display:'flex', flexWrap:'wrap', gap:6, marginBottom:8}}>
                  {suggestedTimes.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setScheduledAt(s.value)}
                      style={{
                        padding:'5px 10px', borderRadius:16, fontSize:11, fontWeight:500,
                        cursor:'pointer', fontFamily:'var(--font-body)', transition:'all 0.15s',
                        background: scheduledAt === s.value ? 'var(--accent)' : 'var(--surface2)',
                        color: scheduledAt === s.value ? '#000' : 'var(--text-muted)',
                        border: scheduledAt === s.value ? '1px solid var(--accent)' : '1px solid var(--border)',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              <input type="datetime-local" value={scheduledAt} onChange={e=>setScheduledAt(e.target.value)} style={inputStyle}/>
              <div style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>Leave blank to add as a pending clip for manual scheduling</div>
            </div>

            {error && (
              <div style={{fontSize:13, color:'#ff4444', padding:'10px 14px', background:'rgba(255,68,68,0.06)', borderRadius:6}}>
                {error}
              </div>
            )}

            {uploading && (
              <div>
                <div style={{height:4, background:'var(--border)', borderRadius:2, overflow:'hidden', marginBottom:6}}>
                  <div style={{height:'100%', background:'var(--accent)', borderRadius:2, width:`${progress}%`, transition:'width 0.3s'}}/>
                </div>
                <div style={{fontSize:12, color:'var(--text-muted)'}}>{progress < 95 ? `Uploading... ${progress}%` : 'Saving...'}</div>
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !file}
              style={{marginTop:4}}
            >
              {uploading ? 'Uploading...' : scheduledAt ? 'Upload & Schedule →' : 'Upload to pending clips →'}
            </button>

            <button className="btn btn-ghost" onClick={() => navigate('/clips')} disabled={uploading}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
